import { Button, Input, Dropdown, Tooltip } from "antd";
import "./styles.css";
import { useState, useCallback } from "react";
import { Icon } from "../icon/icon";
import debounce from "../../common/utils/debounce";
import { MenuOutlined } from "@ant-design/icons";
import { useSidebarContext } from "../sidebar/sidebar-context";

// An apply action may say why it is off; a disabled button needs a wrapper
// for its tooltip to show.
const withTooltip = (item, button, key) => (item.tooltip ? (
  <Tooltip key={key} title={item.tooltip}>
    <span>{button}</span>
  </Tooltip>
) : button);

const Header = ({
  filters,
  stackedFilters,
  title,
  search,
  create,
  apply,
  cancel,
  menuItems = [],
  menu,
  className,
  isBold,
  period,
  editable,
  isEditing,
  onToggleEdit,
  onSave,
  onCancelEdit,
  saveDisabled,
}) => {
  // LF360-2024: the header shows what the page hands it. Each page passes
  // `create` / `editable` only to a user holding the permission the action
  // needs, since that is a different permission on every page.
  const sidebarCtx = useSidebarContext();
  // Only render the hamburger when a real <Sidebar /> is mounted — pages like
  // the dashboard have no sub-menu, so the button would have nothing to toggle.
  const hamburger = sidebarCtx?.hasSider ? (
    <button
      className="lf-sidebar__hamburger--inline"
      onClick={() => sidebarCtx.setIsOpen((v) => !v)}
      aria-label="Toggle menu"
    >
      <MenuOutlined />
    </button>
  ) : null;

  const [inputValue, setInputValue] = useState(search?.value || "");

  const debouncedOnChange = useCallback(
    debounce((value) => {
      if (search?.onChange) search.onChange(value);
    }, 400),
    [search?.onChange]
  );

  const handleChange = (e) => {
    const newValue = e?.target?.value ?? "";
    setInputValue(newValue);
    debouncedOnChange(newValue || null);
  };

  const hasAnyMenu = Array.isArray(menuItems) && menuItems.length > 0;

  const mergedMenuProps = hasAnyMenu
    ? {
        ...(menu || {}),
        items: menuItems.map(({ onClick, ...rest }) => rest),
        onClick: (info) => {
          info.domEvent?.stopPropagation?.();

          const item = menuItems.find((i) => i.key === info.key);
          item?.onClick?.(info);

          menu?.onClick?.(info);
        },
      }
    : undefined;

  // The right block normally contains chunky controls (search input, primary
  // buttons) and is forced onto its own row on mobile. When the page only
  // shows the editable header icons (menu + pencil) the block is tiny and
  // should stay inline with the title — flagged via a modifier class.
  const isCompactRight =
    !search && !create && !apply && !cancel && !isEditing;

  const rightBlock = (
    <div
      className={`assets-header__right${
        isCompactRight ? " assets-header__right--compact" : ""
      }`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexShrink: 0,
      }}
    >
      {editable && !isEditing && (
        <>
          {hasAnyMenu && (
            <Dropdown trigger={["click"]} menu={mergedMenuProps}>
              <Button
                type="text"
                size="middle"
                icon={<Icon name="dots" />}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          )}
          <Button
            type="text"
            size="middle"
            onClick={onToggleEdit}
            icon={<Icon name="edit" />}
          />
        </>
      )}

      {search && (
        <Input
          rootClassName={"assets-header__search"}
          value={inputValue}
          onChange={handleChange}
          size="large"
          placeholder={search.placeholder}
          prefix={<Icon name={"search"} color={"text-1"} />}
          className="assets-header__search"
          maxLength={1000}
          disabled={isEditing}
          style={{ width: 180 }}
          data-testid={search.testID}
        />
      )}

      {isEditing ? (
        <>
          <Button
            className="assets-header__cancel-btn"
            style={{ background: "rgba(108, 108, 137, 0.08)" }}
            onClick={onCancelEdit}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="assets-header__add-btn"
            onClick={onSave}
            disabled={!!saveDisabled}
          >
            Save
          </Button>
        </>
      ) : (
        <>
          {create && (
            <Button
              type={create.type || "primary"}
              className="assets-header__add-btn"
              onClick={create.onClick}
              data-testid={create.testID}
            >
              {create.label}
            </Button>
          )}

          {Array.isArray(apply)
            ? apply.map((item, index) => withTooltip(item, (
                <Button
                  key={item.key || item.label || index}
                  type={item.type || "primary"}
                  className="assets-header__apply-btn"
                  onClick={item.onClick}
                  disabled={item.disabled}
                  loading={item.loading}
                  data-testid={item.testID}
                >
                  {item.label}
                </Button>
              ), item.key || item.label || index))
            : apply && (
                <Button
                  type={apply.type || "primary"}
                  className="assets-header__apply-btn"
                  onClick={apply.onClick}
                  disabled={apply.disabled}
                  loading={apply.loading}
                >
                  {apply.label}
                </Button>
              )}

          {cancel && (
            <Button
              type={cancel.type || "default"}
              className="assets-header__cancel-btn"
              style={{ background: "rgba(108, 108, 137, 0.08)" }}
              onClick={cancel.onClick}
              disabled={cancel.disabled}
              data-testid={cancel.testID}
            >
              {cancel.label}
            </Button>
          )}

          {create && create.renderModalComponent}
        </>
      )}
    </div>
  );

  if (stackedFilters) {
    return (
      <div className={`assets-header ${className || ""}`}>
        <div
          className="assets-header__wrapper"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 12,
          }}
        >
          <div
            className="assets-header__row"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              minWidth: 0,
            }}
          >
            <div
              className="assets-header__left"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                minWidth: 0,
                flex: 1,
              }}
            >
              <div className="assets-header__title-group">
                {hamburger}
                {isBold ? (
                  <h2 className="assets-header__title" data-testid="page-title">{title}</h2>
                ) : (
                  <h3 className="assets-header__title" style={{ opacity: 0.6 }} data-testid="page-title">
                    {title}
                  </h3>
                )}
              </div>
              {period}
            </div>

            {rightBlock}
          </div>

          <div
            className="assets-header__stacked-filters"
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              minWidth: 0,
            }}
          >
            {stackedFilters}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`assets-header ${className || ""}`}>
      <div className="assets-header__wrapper">
        <div className="assets-header__left">
          <div className="assets-header__title-group">
            {hamburger}
            {isBold ? (
              <h2 className="assets-header__title" data-testid="page-title">{title}</h2>
            ) : (
              <h3 className="assets-header__title" style={{ opacity: 0.6 }} data-testid="page-title">
                {title}
              </h3>
            )}
          </div>
          {filters}
          {period}
        </div>

        {rightBlock}
      </div>
    </div>
  );
};

export default Header;